import { useCallback, useEffect, useRef, useState } from "react";
import { MODAL_SLIDE_CLOSE_MS } from "@constants/modalAnimation";

/**
 * 全站 Modal 由下淡入／往下滑出：先加 .closing，延遲 closeMs 後執行 onClosed。
 *
 * @returns {{ isClosing: boolean, closingClassName: string, shouldRender: boolean, requestClose: (afterClose?: () => void) => void }}
 * requestClose 勿直接綁 onClick（會收到 SyntheticEvent）；請用 () => requestClose() 或 handleClose 包一層。
 */
export default function useModalSlideClose({
  isOpen = true,
  closeMs = MODAL_SLIDE_CLOSE_MS,
  closingClass = "closing",
  onClosed,
} = {}) {
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      if (closeTimerRef.current != null) {
        window.clearTimeout(closeTimerRef.current);
      }
    },
    []
  );

  const requestClose = useCallback(
    (afterClose) => {
      if (isClosing) return;
      setIsClosing(true);
      const callback =
        typeof afterClose === "function" ? afterClose : onClosed;
      closeTimerRef.current = window.setTimeout(() => {
        closeTimerRef.current = null;
        setIsClosing(false);
        if (typeof callback === "function") {
          callback();
        }
      }, closeMs);
    },
    [closeMs, isClosing, onClosed]
  );

  const closingClassName = isClosing ? closingClass : "";
  const shouldRender = isOpen || isClosing;

  return {
    isClosing,
    closingClassName,
    shouldRender,
    requestClose,
  };
}
